import { useState, useRef, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { bannerTemplates, getBannerImageUrl } from "@/mocks/bannerTemplates";
import { logActivity } from "@/lib/activityLog";
import type { MyBanner } from "./MyBannersList";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

interface Props {
  banner: MyBanner;
  member: { id: number; full_name: string; email: string };
  onClose: () => void;
  onSaved: () => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.toString().split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function generateApprovalToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function buildSiteBaseUrl(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${pathPrefix}`;
}

function stripTitlePrefix(title: string): { prefix: string; label: string; raw: string } {
  const match = title.match(/^\[([^\]]*)\]\s*/);
  if (match) {
    return { prefix: match[0].trim(), label: match[1], raw: title.slice(match[0].length) };
  }
  return { prefix: "", label: "", raw: title };
}

export default function BannerEditModal({ banner, member, onClose, onSaved }: Props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonLink, setButtonLink] = useState("");

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [customImageUrl, setCustomImageUrl] = useState("");
  const [customImagePreview, setCustomImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const customImageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const { raw } = stripTitlePrefix(banner.title);
    setTitle(raw);
    setDescription(banner.subtitle || "");
    setButtonText(banner.button_text || "");
    setButtonLink(banner.button_link || "");
  }, [banner]);

  const currentTemplateId = selectedTemplateId || (!customImageUrl ? banner.template_id : "");
  const isUpload = !currentTemplateId;
  const selectedTemplate = bannerTemplates.find((t) => t.id === currentTemplateId) || null;
  const previewImage =
    customImagePreview ||
    (selectedTemplate ? getBannerImageUrl(selectedTemplate) : banner.image_url);

  const handleCustomImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Formato inválido. Use apenas JPG, PNG ou WebP.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError("Imagem muito grande. O limite é 5MB.");
      e.target.value = "";
      return;
    }

    setImageError("");
    setError("");
    setCustomImagePreview(URL.createObjectURL(file));
    setSelectedTemplateId("");
    setUploadingImage(true);

    try {
      const base64 = await fileToBase64(file);
      const { data, error: fnError } = await supabase.functions.invoke("upload-banner-image", {
        body: {
          filename: file.name,
          mimeType: file.type,
          base64Data: base64,
        },
      });

      if (fnError || !data || !data.success) {
        setImageError(data?.error || "Erro ao enviar a imagem. Tente novamente.");
        setCustomImagePreview("");
        setCustomImageUrl("");
      } else {
        setCustomImageUrl(data.url);
      }
    } catch {
      setImageError("Erro ao enviar a imagem. Tente novamente.");
      setCustomImagePreview("");
      setCustomImageUrl("");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setCustomImageUrl("");
    setCustomImagePreview("");
    setImageError("");
    setError("");
  };

  const handlePrimaryAction = () => {
    setError("");
    if (!title.trim()) {
      setError("O nome do anúncio é obrigatório.");
      return;
    }
    if (!isUpload && !description.trim()) {
      setError("A descrição do anúncio é obrigatória.");
      return;
    }
    setShowConfirm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    const finalImage =
      customImageUrl || (selectedTemplate ? getBannerImageUrl(selectedTemplate) : banner.image_url);

    const { label } = stripTitlePrefix(banner.title);
    const newTitle = isUpload
      ? title.trim()
      : label
        ? `[${label}] ${title.trim()}`
        : title.trim();
    const newToken = generateApprovalToken();
    const baseUrl = buildSiteBaseUrl();
    const categoryLabel = label || banner.category || "Anúncio";

    const { error: updateErr } = await supabase
      .from("banners")
      .update({
        title: newTitle,
        subtitle: isUpload ? description.trim() || null : description.trim(),
        button_text: isUpload
          ? buttonText.trim() || null
          : buttonText.trim() || "Saiba mais",
        button_link: buttonLink.trim() || "/enviar-anuncio",
        image_url: finalImage,
        template_id: currentTemplateId || null,
        category: selectedTemplate ? selectedTemplate.category : null,
        active: false,
        moderation_status: "pending",
        approval_token: newToken,
        reviewed_at: null,
      })
      .eq("id", banner.id)
      .eq("member_id", member.id);

    if (updateErr) {
      setError("Erro ao salvar as alterações. Tente novamente.");
      setSaving(false);
      setShowConfirm(false);
      return;
    }

    const contact = banner.contact_info || {};

    // Email de moderação para a Ana (com indicação de que é uma EDIÇÃO)
    supabase.functions
      .invoke("notify-banner-moderation", {
        body: {
          baseUrl,
          bannerId: banner.id,
          title: newTitle,
          description: description.trim(),
          category: categoryLabel,
          buttonLink: buttonLink.trim() || "/enviar-anuncio",
          imageUrl: finalImage,
          memberName: member.full_name,
          memberId: member.id,
          contactName: contact.name || member.full_name,
          contactWhatsapp: contact.whatsapp || "",
          contactEmail: contact.email || member.email,
          approvalToken: newToken,
          isEdit: true,
        },
      })
      .then(({ error: notifyError }) => {
        if (notifyError) console.error("Falha ao notificar admin sobre edição:", notifyError);
      });

    // Confirmação para a própria membro (deixando claro que é uma ALTERAÇÃO)
    supabase.functions
      .invoke("notify-banner-published", {
        body: {
          memberName: member.full_name,
          memberEmail: member.email,
          bannerTitle: newTitle,
          isEdit: true,
        },
      })
      .then(({ error: publishError }) => {
        if (publishError) console.error("Falha ao confirmar edição à membro:", publishError);
      });

    logActivity("banner_edit", { banner_id: banner.id, member_id: member.id });

    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-nude px-6 md:px-8 py-4 flex items-center justify-between z-10">
          <h3 className="font-display text-lg md:text-xl text-brown">Editar anúncio</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-brown/50 hover:text-brown hover:bg-beige transition-colors cursor-pointer"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          {/* Aviso de re-moderação */}
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200">
            <i className="ri-information-line text-amber-600 mt-0.5 shrink-0" />
            <p className="font-body text-xs text-amber-800 leading-relaxed">
              <strong>Atenção:</strong> ao salvar qualquer alteração, seu anúncio{" "}
              <strong>sai do ar</strong> e volta para a análise da administradora antes de ser
              publicado novamente.
            </p>
          </div>

          {isUpload && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-beige/60 border border-nude">
              <i className="ri-information-line text-brown/50 mt-0.5 shrink-0" />
              <p className="font-body text-xs text-brown/70 leading-relaxed">
                Sua arte já tem os textos? Deixe a descrição e o texto do botão em branco
                para não cobrir a imagem. O nome do anúncio é usado apenas internamente.
              </p>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Título */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              {isUpload ? (
                <>
                  Nome do anúncio{" "}
                  <span className="text-brown/40 font-normal">
                    (uso interno, não aparece no site)
                  </span>{" "}
                  <span className="text-primary">*</span>
                </>
              ) : (
                <>
                  Título do anúncio <span className="text-primary">*</span>
                </>
              )}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setError("");
              }}
              maxLength={80}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Título do seu anúncio"
            />
            <p className="font-body text-xs text-brown/40 mt-1 text-right">{title.length}/80</p>
          </div>

          {/* Descrição */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Descrição{" "}
              {isUpload ? (
                <span className="text-brown/40 font-normal">(opcional)</span>
              ) : (
                <span className="text-primary">*</span>
              )}
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setError("");
              }}
              rows={4}
              maxLength={300}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
              placeholder="Descreva seu negócio, vaga ou o que você está procurando."
            />
            <p className="font-body text-xs text-brown/40 mt-1 text-right">
              {description.length}/300
            </p>
          </div>

          {/* Imagem */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Imagem do anúncio
            </label>
            <div className="aspect-[1440/400] w-full rounded-lg overflow-hidden bg-beige mb-4">
              {previewImage ? (
                <img src={previewImage} alt="Prévia do anúncio" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-brown/40">
                  <span className="font-body text-sm">Sem imagem</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Upload própria imagem */}
              <button
                type="button"
                onClick={() => customImageInputRef.current?.click()}
                className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer text-left ${
                  customImageUrl
                    ? "border-primary ring-2 ring-primary/30"
                    : "border-dashed border-nude hover:border-primary/40"
                }`}
              >
                <div className="aspect-[1440/400] w-full relative bg-beige/50">
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3">
                    {uploadingImage ? (
                      <>
                        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span className="font-body text-xs text-brown/60">Enviando imagem...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-cloud-2-line text-2xl text-brown/40" />
                        <span className="font-body text-xs text-brown/60 text-center leading-snug">
                          Enviar minha imagem
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {customImageUrl && !uploadingImage && (
                  <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-primary text-white">
                    <i className="ri-check-line text-xs" />
                  </div>
                )}
              </button>
              <input
                ref={customImageInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handleCustomImage}
                className="hidden"
              />

              {bannerTemplates.map((tpl) => {
                const isSelected = selectedTemplateId === tpl.id;
                const isPalette = !!tpl.palette;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => handleTemplateSelect(tpl.id)}
                    className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer text-left ${
                      isSelected
                        ? "border-primary ring-2 ring-primary/30"
                        : "border-nude hover:border-primary/40"
                    }`}
                  >
                    {isPalette && tpl.palette ? (
                      <>
                        <div className="aspect-[1440/400] w-full overflow-hidden">
                          <div
                            className="w-full h-full transition-transform duration-300 group-hover:scale-[1.03]"
                            style={{
                              background: `linear-gradient(135deg, ${tpl.palette.primary} 0%, ${tpl.palette.accent} 50%, ${tpl.palette.background} 100%)`,
                            }}
                          />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent p-3">
                          <span className="font-body text-xs text-white font-medium tracking-wide">
                            {tpl.category}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="aspect-[1440/400] w-full overflow-hidden">
                          <img
                            src={tpl.image_url}
                            alt={tpl.label}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-3">
                          <span className="font-body text-xs text-white font-medium tracking-wide">
                            {tpl.category}
                          </span>
                        </div>
                      </>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-primary text-white">
                        <i className="ri-check-line text-xs" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {imageError && (
              <p className="font-body text-xs text-red-600 mt-3 flex items-center gap-1.5">
                <i className="ri-error-warning-line" />
                {imageError}
              </p>
            )}
          </div>

          {/* Link do botão */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Link do botão <span className="text-brown/40 font-normal">(opcional)</span>
            </label>
            <input
              type="url"
              value={buttonLink}
              onChange={(e) => {
                setButtonLink(e.target.value);
                setError("");
              }}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="https://instagram.com/seunegocio"
            />
          </div>

          {/* Texto do botão */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Texto do botão{" "}
              {isUpload && (
                <span className="text-brown/40 font-normal">(opcional)</span>
              )}
            </label>
            <input
              type="text"
              value={buttonText}
              onChange={(e) => {
                setButtonText(e.target.value);
                setError("");
              }}
              maxLength={40}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Quero conhecer"
            />
            <p className="font-body text-xs text-brown/40 mt-1">{buttonText.length}/40</p>
          </div>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-nude px-6 md:px-8 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="font-body text-sm px-6 py-3 rounded-full border border-nude text-brown tracking-wide uppercase hover:bg-beige transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handlePrimaryAction}
            disabled={saving || uploadingImage}
            className="font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Salvar alterações
          </button>
        </div>

        {/* Confirmação de re-moderação */}
        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowConfirm(false)} />
            <div className="relative bg-white rounded-2xl p-8 w-full max-w-md text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-5">
                <i className="ri-error-warning-line text-xl text-amber-500" />
              </div>
              <h3 className="font-display text-lg text-brown mb-2">Seu anúncio vai sair do ar</h3>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                Ao editar, seu anúncio sai do ar e volta para a análise da administradora.
                Ele só voltará a aparecer depois de aprovado novamente. Deseja continuar?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="font-body text-sm px-6 py-3 rounded-full bg-primary text-white tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Salvando...
                    </span>
                  ) : (
                    "Sim, salvar e enviar"
                  )}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={saving}
                  className="font-body text-sm px-6 py-3 rounded-full border border-nude text-brown tracking-wide uppercase hover:bg-beige transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}